/**
 * Project 750k API Service
 * Handles all API calls for the Project 750k UCC data system
 */

import apiService from '../../../services/apiService';

/**
 * List UCC data with pagination and optional search
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term (optional)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 50)
 * @returns {Promise<Object>} - { data, pagination }
 */
export const listUccData = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const queryString = queryParams.toString();
    const url = `/project750k/ucc-data${queryString ? `?${queryString}` : ''}`;
    
    // apiService.get() already returns response.data, which is {data: [...], pagination: {...}}
    const result = await apiService.get(url);
    console.log('🔍 API result:', result);
    console.log('🔍 result.data array:', result.data);
    console.log('🔍 result.pagination:', result.pagination);
    return {
      data: result.data || [],
      pagination: result.pagination || { page: 1, pageSize: 50, totalItems: 0, totalPages: 0 }
    };
  } catch (error) {
    console.error('Error listing UCC data:', error);
    throw error;
  }
};

/**
 * Export UCC data to Excel (current page only)
 * @param {string} searchTerm - Optional search term to filter export
 * @param {number} page - Current page number
 * @param {number} pageSize - Items per page
 * @returns {Promise<boolean>} - Success status
 */
export const exportUccDataExcel = async (searchTerm = '', page = 1, pageSize = 500) => {
  try {
    const queryParams = new URLSearchParams();
    if (searchTerm) queryParams.append('search', searchTerm);
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());
    
    const queryString = queryParams.toString();
    const url = `/project750k/ucc-data/export${queryString ? `?${queryString}` : ''}`;
    
    console.log('📊 Requesting Excel export from:', url);
    
    // Get presigned download URL from backend
    const data = await apiService.get(url);
    
    console.log('📊 Export data received:', data);
    
    if (data && data.downloadUrl) {
      // Download from S3 presigned URL
      const filename = data.filename || `UCC_Data_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      console.log('📥 Downloading from S3:', filename);
      
      // Fetch the file from S3
      const fileResponse = await fetch(data.downloadUrl);
      
      if (!fileResponse.ok) {
        throw new Error(`S3 download failed: ${fileResponse.status} ${fileResponse.statusText}`);
      }
      
      const blob = await fileResponse.blob();
      console.log('✅ Downloaded blob size:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } else {
      console.error('❌ Invalid data structure:', data);
      throw new Error('Invalid response from server - no downloadUrl');
    }
  } catch (error) {
    console.error('Error exporting UCC data:', error);
    throw error;
  }
};

/**
 * List UCC Master List (ListC) data with pagination and optional search
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term (optional)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 500)
 * @returns {Promise<Object>} - { data, pagination }
 */
export const listUccDataListC = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const queryString = queryParams.toString();
    const url = `/project750k/ucc-data-listc${queryString ? `?${queryString}` : ''}`;
    
    const result = await apiService.get(url);
    console.log('🔍 UCC ListC API result:', result);
    return {
      data: result.data || [],
      pagination: result.pagination || { page: 1, pageSize: 500, totalItems: 0, totalPages: 0 }
    };
  } catch (error) {
    console.error('Error listing UCC ListC data:', error);
    throw error;
  }
};

/**
 * Export UCC Master List (ListC) data to Excel
 * @param {string} searchTerm - Optional search term to filter export
 * @param {number} page - Current page number
 * @param {number} pageSize - Items per page
 * @returns {Promise<boolean>} - Success status
 */
export const exportUccDataListCExcel = async (searchTerm = '', page = 1, pageSize = 500) => {
  try {
    const queryParams = new URLSearchParams();
    if (searchTerm) queryParams.append('search', searchTerm);
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());
    
    const queryString = queryParams.toString();
    const url = `/project750k/ucc-data-listc/export${queryString ? `?${queryString}` : ''}`;
    
    console.log('📊 Requesting UCC ListC Excel export from:', url);
    
    // Get presigned download URL from backend
    const data = await apiService.get(url);
    
    console.log('📊 Export data received:', data);
    
    if (data && data.downloadUrl) {
      const filename = data.filename || `UCC_MasterList_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      console.log('📥 Downloading from S3:', filename);
      
      // Fetch the file from S3
      const fileResponse = await fetch(data.downloadUrl);
      
      if (!fileResponse.ok) {
        throw new Error(`S3 download failed: ${fileResponse.status} ${fileResponse.statusText}`);
      }
      
      const blob = await fileResponse.blob();
      console.log('✅ Downloaded blob size:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    } else {
      console.error('❌ Invalid data structure:', data);
      throw new Error('Invalid response from server - no downloadUrl');
    }
  } catch (error) {
    console.error('Error exporting UCC ListC data:', error);
    throw error;
  }
};

/**
 * Get debtor filings report
 * @param {string} debtorName - Debtor name to search for
 * @param {string} dataset - Which dataset to search ('all', 'lb4', 'listc')
 * @returns {Promise<Object>} - { filings, summary }
 */
export const getDebtorFilings = async (debtorName, dataset = 'all') => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('name', debtorName);
    queryParams.append('dataset', dataset);

    const url = `/project750k/reports/debtor-filings?${queryParams.toString()}`;
    const result = await apiService.get(url);
    
    return result;
  } catch (error) {
    console.error('Error fetching debtor filings:', error);
    throw error;
  }
};

/**
 * Get regional analysis report
 * @param {Object} filters - Optional filters { product, lender }
 * @returns {Promise<Object>} - { counties, chartData, summary }
 */
export const getRegionalAnalysis = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.product) queryParams.append('product', filters.product);
    if (filters.lender) queryParams.append('lender', filters.lender);

    const url = `/project750k/reports/regional-analysis${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const result = await apiService.get(url);
    
    return result;
  } catch (error) {
    console.error('Error fetching regional analysis:', error);
    throw error;
  }
};

/**
 * Get top debtors report
 * @param {number} limit - Number of top debtors to return
 * @param {number} minFilings - Minimum number of filings to include
 * @returns {Promise<Object>} - { debtors, chartData, summary }
 */
export const getTopDebtors = async (limit = 20, minFilings = 2) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit.toString());
    queryParams.append('minFilings', minFilings.toString());

    const url = `/project750k/reports/top-debtors?${queryParams.toString()}`;
    const result = await apiService.get(url);
    
    return result;
  } catch (error) {
    console.error('Error fetching top debtors:', error);
    throw error;
  }
};

/**
 * Get secured party analysis report
 * @param {string} county - Optional county filter
 * @returns {Promise<Object>} - { securedParties, chartData, summary }
 */
export const getSecuredPartyAnalysis = async (county = null) => {
  try {
    const queryParams = new URLSearchParams();
    if (county) queryParams.append('county', county);

    const url = `/project750k/reports/secured-parties${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const result = await apiService.get(url);
    
    return result;
  } catch (error) {
    console.error('Error fetching secured party analysis:', error);
    throw error;
  }
};

/**
 * Get amendment trends report
 * @param {Object} filters - Optional filters { year, county }
 * @returns {Promise<Object>} - { trends, chartData, recentAmendments, summary }
 */
export const getAmendmentTrends = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.year) queryParams.append('year', filters.year);
    if (filters.county) queryParams.append('county', filters.county);

    const url = `/project750k/reports/amendments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const result = await apiService.get(url);
    
    return result;
  } catch (error) {
    console.error('Error fetching amendment trends:', error);
    throw error;
  }
};

export default {
  listUccData,
  exportUccDataExcel,
  listUccDataListC,
  exportUccDataListCExcel,
  getDebtorFilings,
  getRegionalAnalysis,
  getTopDebtors,
  getSecuredPartyAnalysis,
  getAmendmentTrends
};

